import { motion } from "framer-motion";
import { Users, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import { FLEET, BRAND } from "../lib/brand";
import { useT } from "../lib/i18n";

function FleetCard({ item, index, onBook }) {
  const { t } = useT();
  const isLeft = index % 2 === 0;
  const variant = t(`fleet.variants.${item.id}`, item.variant);
  const tag = t(`fleet.tags.${item.tag}`, item.tag);

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="grid md:grid-cols-12 gap-6 md:gap-10 items-center py-8 md:py-12 border-b border-white/10"
      data-testid={`fleet-card-${item.id}`}
    >
      {/* Image */}
      <div className={`md:col-span-7 relative ${isLeft ? "md:order-1" : "md:order-2"}`}>
        <div className="relative aspect-[16/10] overflow-hidden bg-[color:var(--surface)] border border-white/10">
          <motion.img
            src={item.image}
            alt={`${item.name} ${variant}`}
            loading="lazy"
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
          <div className="absolute top-3 left-3 bg-[color:var(--primary)] text-white text-[9px] font-bold uppercase tracking-[0.22em] px-2 py-1 font-mono">
            {tag}
          </div>
          <div className="absolute bottom-3 right-3 border border-white/30 bg-black/60 backdrop-blur text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 font-mono">
            {item.hp}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.24em] font-mono text-white/50">
          <span>0{index + 1} / 0{FLEET.length}</span>
          <span>{variant}</span>
        </div>
      </div>

      {/* Details */}
      <div className={`md:col-span-5 ${isLeft ? "md:order-2" : "md:order-1"}`}>
        <p className="overline mb-3">{t("fleet.cardOverline")}</p>
        <h3
          className="font-display text-white text-3xl md:text-4xl lg:text-5xl leading-[0.95]"
          data-testid={`fleet-name-${item.id}`}
        >
          {item.name}
        </h3>
        <p className="mt-1.5 text-sm md:text-base text-[color:var(--text-muted)]">{variant}</p>

        {/* Spec chips */}
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono text-white/80">
            <Users size={12} /> {item.seats === 1 ? t("fleet.oneDriver") : `${item.seats} ${t("fleet.seatsSuffix")}`}
          </span>
          <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono text-white/80">
            <ShieldCheck size={12} /> {t("fleet.minAgePrefix")} {item.minAge}+
          </span>
          <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono text-white/80">
            <Clock size={12} /> {t("fleet.guided")}
          </span>
        </div>

        {/* Pricing */}
        <div className="mt-6 border-t border-white/15">
          <div className="grid grid-cols-2 divide-x divide-white/15">
            <div className="py-4">
              <p className="text-[9px] uppercase tracking-[0.28em] font-mono text-[color:var(--primary)]">{t("fleet.oneHour")}</p>
              <p className="font-display text-white text-4xl md:text-5xl mt-1">${item.hourly}</p>
            </div>
            <div className="py-4 pl-4">
              <p className="text-[9px] uppercase tracking-[0.28em] font-mono text-white/60">{t("fleet.twoHour")}</p>
              <p className="font-display text-white text-4xl md:text-5xl mt-1">${item.twoHour}</p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/60">
            <span>
              {t("fleet.deposit")}: <strong className="text-white">${item.deposit}</strong>
            </span>
            {item.passenger && (
              <span>
                {t("fleet.passenger")}: <strong className="text-white">${item.passenger}</strong>
              </span>
            )}
          </div>
        </div>

        <a
          href={BRAND.bookingUrl}
          className="mt-6 inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
          data-testid={`fleet-book-${item.id}`}
        >
          {t("fleet.bookThisRide")} <ArrowRight size={14} />
        </a>
      </div>
    </motion.article>
  );
}

export default function Fleet({ onOpenBookText }) {
  const { t } = useT();
  const sub = t("fleet.subhead");
  return (
    <section id="fleet" className="bg-black py-20 md:py-28" data-testid="fleet-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
          <div className="md:col-span-8">
            <p className="overline mb-3">{t("fleet.overline")}</p>
            <h2
              className="font-display text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9]"
              data-testid="fleet-heading"
            >
              {t("fleet.headingLine1")} <span className="text-[color:var(--primary)]">{t("fleet.headingAmp")}</span>
              <br /> {t("fleet.headingLine2")}
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-sm md:text-base text-[color:var(--text-muted)] leading-relaxed">
              {sub[0]}
              <strong className="text-white">{sub[1]}</strong>
              {sub[2]}
            </p>
          </div>
        </div>

        <div>
          {FLEET.map((item, i) => (
            <FleetCard key={item.id} item={item} index={i} onBook={onOpenBookText} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-10 border border-white/10 bg-[color:var(--bg-elev)] px-5 md:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <p className="overline text-[color:var(--primary)] mb-1.5">{t("fleet.paymentOverline")}</p>
            <p className="text-lg md:text-xl font-display text-white">
              {t("fleet.paymentTitle")}
            </p>
          </div>
          <button
            onClick={onOpenBookText}
            className="shrink-0 inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
            data-testid="fleet-cta-book"
          >
            {t("fleet.textUsToBook")} <ArrowRight size={14} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
